import { ACTION_TERMS } from 'modules/featureFlags/constants';
import { Actions, Nouns } from 'modules/featureFlags/types';
import { formatRow } from 'modules/featureFlags/utils';
import styled from 'styled-components';
import { Overline, smallShadow } from 'ui/theme';
import { ConfirmationCardRow } from './ConfirmationCardRow';

const StyledCard = styled.div`
  overflow: hidden;
  background-color: #fff;
  border: ${({ theme }) => `${theme.spacing.values.single} solid ${theme.colors.neutrals.grey5}`};
  border-radius: ${({ theme }) => theme.spacing.values.half};
  ${smallShadow}
`;

const Header = styled(Overline)`
  display: block;
  background-color: ${({ theme: { colors } }) => colors.neutrals.white};
  padding: ${({ theme: { spacing } }) => `${spacing.values.threeQuarter} ${spacing.values.base}`};

  [data-variant=${Actions.Enabled}] & {
    color: ${({ theme: { colors } }) => colors.primaryColors.green100};
    background-color: ${({ theme: { colors } }) => colors.primaryColors.green10};
  }

  [data-variant=${Actions.Disabled}] & {
    color: ${({ theme: { colors } }) => colors.primaryColors.red100};
    background-color: ${({ theme: { colors } }) => colors.primaryColors.red10};
  }
`;

const Body = styled.div`
  padding: ${({ theme }) => `${theme.spacing.values.threeQuarter} ${theme.spacing.values.base}`};
`;

type Props = {
  action: Actions | undefined;
  currentOrgIds?: number[];
  updatedOrgIds?: number[];
  currentProjects?: number[];
  updatedProjects?: number[];
  variant: Actions;
};

export const ConfirmationCard = ({
  action,
  currentOrgIds = [],
  updatedOrgIds = [],
  currentProjects = [],
  updatedProjects = [],
  variant,
}: Props) => {
  // if action === variant => don't show
  // ie. if Enabling, don't show enabled card
  const isGlobalChange = action === variant;
  const isOppositeAction = action === ACTION_TERMS[variant].oppositeAction;
  // changes state
  const {
    added: addedOrgs,
    removed: removedOrgs,
    total: totalOrgs,
  } = formatRow(currentOrgIds, updatedOrgIds);
  const {
    added: addedProjects,
    removed: removedProjects,
    total: totalProjects,
  } = formatRow(currentProjects, updatedProjects);
  const showOrgs = isOppositeAction || addedOrgs + removedOrgs > 0;
  const showProjects = isOppositeAction || addedProjects + removedProjects > 0;

  // don't show card if global action equals variant (ie. enable globally, don't show enabled card details)
  // or if there are manual changes but not to either these orgs or projects
  if (isGlobalChange || (!showOrgs && !showProjects)) {
    return <></>;
  }

  return (
    <>
      <StyledCard data-variant={variant}>
        <Header>{variant}</Header>
        <Body>
          {showOrgs && (
            <ConfirmationCardRow
              added={addedOrgs}
              removed={removedOrgs}
              total={totalOrgs}
              noun={Nouns.Org}
              variant={variant}
            />
          )}

          {showProjects && (
            <ConfirmationCardRow
              added={addedProjects}
              removed={removedProjects}
              total={totalProjects}
              noun={Nouns.Project}
              variant={variant}
            />
          )}
        </Body>
      </StyledCard>
    </>
  );
};
